"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useCallback, Suspense } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Box } from '@/components/atoms/Box';
import { Article } from '@/components/atoms/Article';
import { Slug } from '@/content/types';
import type { SanityImageSource } from '@/components/atoms/SanityImage';
import Link from 'next/link';
import { SanityImage } from '@/components/atoms/SanityImage';
import { getDocumentUrl } from '@/lib/routing';
import { getTypeLabel } from '@/lib/search';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { generateLayeredGradients } from '@/lib/gradients';
import { HeroGradientInfo } from '@/lib/recommendations';
type SearchResultItem = {
  _id: string;
  _type: string;
  slug?: Slug | null;
  title?: string;
  image?: SanityImageSource | null;
  subtitle?: string;
  category?: string;
  heroGradient?: HeroGradientInfo | null;
};
interface SearchResultsProps {
  results: SearchResultItem[];
  total: number;
  query: string;
  page: number;
  totalPages: number;
  limit: number;
}

/**
 * Highlight search terms in text
 */
function HighlightedText(t0) {
  const $ = _c(7);
  const {
    text,
    query
  } = t0;
  if (!query.trim() || !text) {
    let t1;
    if ($[0] !== text) {
      t1 = <>{text}</>;
      $[0] = text;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== query || $[3] !== text) {
    const terms = query.trim().split(/\s+/).filter(Boolean);
    const pattern = new RegExp(`(${terms.map(_temp).join("|")})`, "gi");
    const parts = text.split(pattern);
    t1 = parts.map((part, i) => {
      const isMatch = terms.some(term => part.toLowerCase() === term.toLowerCase());
      return isMatch ? <mark key={i} className="bg-primary/20 text-inherit px-0.5 rounded">{part}</mark> : <span key={i}>{part}</span>;
    });
    $[2] = query;
    $[3] = text;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp(t) {
  return t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
const SearchResultsInner = t0 => {
  const $ = _c(40);
  const {
    results,
    total,
    query,
    page,
    totalPages,
    limit
  } = t0;
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const gridRef = useRef(null);
  let t1;
  if ($[0] !== pathname || $[1] !== router || $[2] !== searchParams) {
    t1 = newPage => {
      const params = new URLSearchParams(searchParams.toString());
      if (newPage === 1) {
        params.delete("page");
      } else {
        params.set("page", newPage.toString());
      }
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname;
      router.push(newUrl, {
        scroll: false
      });
      if (gridRef.current) {
        gridRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    };
    $[0] = pathname;
    $[1] = router;
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const goToPage = t1;
  const startResult = (page - 1) * limit + 1;
  const endResult = Math.min(page * limit, total);
  if (!query) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="text-center py-16"><Search className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" /><p className="text-xl text-muted-foreground">Enter a search term to find content</p></div>;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (results.length === 0) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Search className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== query) {
      t3 = <p className="text-xl text-muted-foreground">No results found for "{query}"</p>;
      $[6] = query;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <p className="text-muted-foreground mt-2">Try different keywords or check your spelling</p>;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== t3) {
      t5 = <div className="text-center py-16">{t2}{t3}{t4}</div>;
      $[9] = t3;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  const t2 = total !== 1 ? "s" : "";
  let t3;
  if ($[11] !== query) {
    t3 = <span className="text-primary">"{query}"</span>;
    $[11] = query;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== total) {
    t4 = <p className="text-2xl font-semibold">{total} Result{t2} for{" "}{t3}</p>;
    $[13] = t2;
    $[14] = t3;
    $[15] = total;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== endResult || $[18] !== startResult || $[19] !== total) {
    t5 = <p className="text-muted-foreground mt-1">Showing {startResult}-{endResult} of {total}</p>;
    $[17] = endResult;
    $[18] = startResult;
    $[19] = total;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t4 || $[22] !== t5) {
    t6 = <div className="mb-8">{t4}{t5}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== query || $[25] !== results) {
    let t8;
    if ($[27] !== query) {
      t8 = item => {
        const imageWithAlt = item.image ? {
          ...item.image,
          alt: item.title || "Search result image"
        } as SanityImageSource : null;
        const heroGradient = item.heroGradient;
        const hasPartnerGradient = item._type === "partner" && heroGradient?.gradientColors?.length && heroGradient?.heroLogo;
        const gradientStyle = hasPartnerGradient ? generateLayeredGradients(heroGradient.gradientColors) : undefined;
        return <Article key={item._id} className="group"><Link href={getDocumentUrl(item._type, item.slug?.current)} className={cn("flex flex-col md:flex-row gap-6 p-6 rounded-2xl border border-border/50 bg-card transition-all duration-300", "hover:border-primary/30 hover:shadow-xl", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2")}>{hasPartnerGradient && gradientStyle ? <div className="relative w-full md:w-1/3 flex-shrink-0 overflow-hidden rounded-xl aspect-landscape bg-black flex items-center justify-center" style={{
              backgroundImage: gradientStyle
            }}><div className="relative flex items-center justify-center p-4"><div className="absolute inset-0 scale-130 blur-2xl opacity-80 rounded-full transition-opacity duration-500 group-hover:opacity-100" style={{
                  backgroundColor: heroGradient.gradientColors[0]
                }} aria-hidden="true" /><SanityImage image={heroGradient.heroLogo as SanityImageSource} width={200} placeholder="empty" className="relative overflow-visible transition-transform duration-500 group-hover:scale-105 [&>img]:max-h-20 [&>img]:w-auto [&>img]:h-auto" sizes="200px" /></div></div> : imageWithAlt ? <div className="relative w-full md:w-1/3 flex-shrink-0 overflow-hidden rounded-xl aspect-landscape"><SanityImage image={imageWithAlt} fill={true} aspectRatio="16/10" sizes="(max-width: 768px) 100vw, 33vw" className="object-cover transition-transform duration-500 group-hover:scale-105" /></div> : null}<div className="flex-1 flex flex-col justify-center min-w-0"><span className="inline-block w-fit px-3 py-1 text-sm font-medium bg-primary/10 text-primary rounded-full mb-3">{getTypeLabel(item._type)}</span><h2 className="font-bold text-xl md:text-2xl leading-tight line-clamp-2 group-hover:text-primary transition-colors"><HighlightedText text={item.title || ""} query={query} /></h2>{item.subtitle && <p className="mt-3 text-base text-muted-foreground line-clamp-3"><HighlightedText text={item.subtitle} query={query} /></p>}</div></Link></Article>;
      };
      $[27] = query;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    t7 = results.map(t8);
    $[24] = query;
    $[25] = results;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[29] !== page || $[30] !== t7) {
    t8 = <Box ref={gridRef} key={page} className="flex flex-col gap-6">{t7}</Box>;
    $[29] = page;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== goToPage || $[33] !== page || $[34] !== totalPages) {
    t9 = totalPages > 1 && <div className="mt-12 flex items-center justify-center gap-2"><button onClick={() => goToPage(page - 1)} disabled={page === 1} className={cn("flex items-center justify-center w-10 h-10 rounded-lg border transition-colors", page === 1 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400")} aria-label="Previous page"><ChevronLeft className="w-5 h-5" /></button><div className="flex items-center gap-1">{Array.from({
          length: totalPages
        }, _temp2).map(pageNum => {
          const showPage = pageNum === 1 || pageNum === totalPages || Math.abs(pageNum - page) <= 1;
          const showEllipsisBefore = pageNum === page - 2 && page > 3;
          const showEllipsisAfter = pageNum === page + 2 && page < totalPages - 2;
          if (showEllipsisBefore || showEllipsisAfter) {
            return <span key={pageNum} className="px-2 text-gray-400">...</span>;
          }
          if (!showPage) {
            return null;
          }
          return <button key={pageNum} onClick={() => goToPage(pageNum)} className={cn("w-10 h-10 rounded-lg border font-medium transition-colors", pageNum === page ? "bg-accent text-accent-foreground border-accent" : "border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400")} aria-label={`Page ${pageNum}`} aria-current={pageNum === page ? "page" : undefined}>{pageNum}</button>;
        })}</div><button onClick={() => goToPage(page + 1)} disabled={page === totalPages} className={cn("flex items-center justify-center w-10 h-10 rounded-lg border transition-colors", page === totalPages ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400")} aria-label="Next page"><ChevronRight className="w-5 h-5" /></button></div>;
    $[32] = goToPage;
    $[33] = page;
    $[34] = totalPages;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t6 || $[37] !== t8 || $[38] !== t9) {
    t10 = <>{t6}{t8}{t9}</>;
    $[36] = t6;
    $[37] = t8;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
};

// Loading fallback
const SearchResultsLoading = () => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-8 bg-muted rounded w-64 mb-2" />;
    t1 = <div className="h-4 bg-muted rounded w-32 mb-8" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="animate-pulse">{t0}{t1}<div className="flex flex-col gap-6">{[1, 2, 3, 4].map(_temp3)}</div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export default function SearchResults(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SearchResultsLoading />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><SearchResultsInner {...props} /></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp2(_, i) {
  return i + 1;
}
function _temp3(i) {
  return <div key={i} className="flex flex-col md:flex-row gap-6 p-6 rounded-2xl border border-border/50"><div className="w-full md:w-1/3 aspect-landscape bg-muted rounded-xl flex-shrink-0" /><div className="flex-1 flex flex-col justify-center"><div className="h-6 bg-muted rounded w-24 mb-3" /><div className="h-8 bg-muted rounded w-full mb-3" /><div className="h-5 bg-muted rounded w-3/4" /></div></div>;
}